// import Alert, { Color } from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
// import Snackbar from "@mui/material/Snackbar";
import { Alert, AlertColor, Snackbar } from "@mui/material";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

interface SnackbarContextInterface {
  error: (newMessage: string) => void;
  success: (newMessage: string) => void;
  warn: (newMessage: string) => void;
}

export const SnackbarContext = createContext({} as SnackbarContextInterface);

type SnackbarProviderProps = {
  children: React.ReactNode;
};

const SnackbarProvider = ({ children }: SnackbarProviderProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const [snackbarSettings, setSnackbarSettings] = useState({
    message: "",
    title: "",
    severity: undefined as AlertColor | undefined,
  });

  const handleClose = () => {
    setOpen(false);
  };

  // Memoised so the context value keeps a stable identity across renders.
  //
  // This was `value={{ error, success, warn }}` -- a fresh object on every render
  // of the provider, which meant every consumer that wanted `snackbar` in a
  // useEffect dependency array had to leave it out (and silence the lint warning)
  // to avoid re-running the effect constantly. With this, `snackbar` can be listed
  // honestly: its identity now changes only when the translation function does,
  // i.e. when the language changes.
  //
  // These close over nothing that varies except `t`; setSnackbarSettings and
  // setOpen are stable useState setters.
  const error = useCallback(
    (newMessage: string) => {
      setSnackbarSettings({
        title: t("common.snackbar.error"),
        message: newMessage,
        severity: "error",
      });
      setOpen(true);
    },
    [t]
  );

  const success = useCallback(
    (newMessage: string) => {
      setSnackbarSettings({
        title: t("common.snackbar.success"),
        message: newMessage,
        severity: "success",
      });
      setOpen(true);
    },
    [t]
  );

  const warn = useCallback((newMessage: string) => {
    setSnackbarSettings({
      title: "Warning",
      message: newMessage,
      severity: "warning",
    });
    setOpen(true);
  }, []);

  const snackbar = useMemo(
    () => ({ error, success, warn }),
    [error, success, warn]
  );

  const { message, title, severity } = snackbarSettings;

  return (
    <SnackbarContext.Provider value={snackbar}>
      {children}
      <Snackbar
        key={message}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={open}
        autoHideDuration={3000}
        onClose={() => handleClose()}
      >
        <Alert onClose={() => handleClose()} severity={severity}>
          <AlertTitle>{title}</AlertTitle>
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export function useSnackbar() {
  return useContext(SnackbarContext);
}

export default SnackbarProvider;
